import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Joyride, { ACTIONS, CallBackProps, EVENTS, STATUS } from 'react-joyride';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import {
  DEMO_PHASE1_TOUR_STEPS,
  DEMO_START_TOUR_EVENT,
  DEMO_TOUR_AUTOSTART_KEY,
  DEMO_TOUR_STORAGE_KEY,
  DemoDashboardSection,
} from '../config/demoTourSteps';

type Props = {
  enabled: boolean;
  activeSection: DemoDashboardSection;
  onSectionChange: (section: DemoDashboardSection) => void;
};

/** Keep the spotlight target in the middle of the viewport (Joyride's default scroll pins too high under our sticky banner). */
function scrollTourTargetIntoView(target: string | HTMLElement | undefined) {
  if (!target || typeof document === 'undefined') return;
  const el =
    typeof target === 'string' ? (document.querySelector(target) as HTMLElement | null) : target;
  if (!el || typeof el.scrollIntoView !== 'function') return;
  el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
}

const DemoGuidedTour: React.FC<Props> = ({ enabled, activeSection, onSectionChange }) => {
  const [chooserOpen, setChooserOpen] = useState(false);
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    try {
      const choice = localStorage.getItem(DEMO_TOUR_STORAGE_KEY);
      if (!choice) {
        setChooserOpen(true);
      }
    } catch {
      setChooserOpen(true);
    }
  }, [enabled]);

  const persistChoice = (choice: 'tour' | 'guide') => {
    try {
      localStorage.setItem(DEMO_TOUR_STORAGE_KEY, choice);
    } catch {
      /* ignore */
    }
  };

  const startTour = useCallback(() => {
    setChooserOpen(false);
    persistChoice('tour');
    setStepIndex(0);
    const first = DEMO_PHASE1_TOUR_STEPS[0];
    if (first?.section) onSectionChange(first.section);
    window.setTimeout(() => {
      scrollTourTargetIntoView(first?.target as string);
      setRun(true);
    }, 350);
  }, [onSectionChange]);

  useEffect(() => {
    if (!enabled) return;

    const onStart = () => startTour();
    window.addEventListener(DEMO_START_TOUR_EVENT, onStart);

    try {
      if (sessionStorage.getItem(DEMO_TOUR_AUTOSTART_KEY) === '1') {
        sessionStorage.removeItem(DEMO_TOUR_AUTOSTART_KEY);
        window.setTimeout(() => startTour(), 200);
      }
    } catch {
      /* ignore */
    }

    return () => window.removeEventListener(DEMO_START_TOUR_EVENT, onStart);
  }, [enabled, startTour]);

  const useGuide = () => {
    persistChoice('guide');
    setChooserOpen(false);
    setRun(false);
  };

  const handleCallback = (data: CallBackProps) => {
    const { action, index, status, type, step } = data;

    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setRun(false);
      setStepIndex(0);
      return;
    }

    if (type === EVENTS.STEP_BEFORE) {
      scrollTourTargetIntoView((step?.target as string) || DEMO_PHASE1_TOUR_STEPS[index]?.target);
      return;
    }

    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);
      if (nextIndex < 0 || nextIndex >= DEMO_PHASE1_TOUR_STEPS.length) {
        setRun(false);
        return;
      }
      const next = DEMO_PHASE1_TOUR_STEPS[nextIndex];
      if (next.section && next.section !== activeSection) {
        onSectionChange(next.section);
        setRun(false);
        window.setTimeout(() => {
          scrollTourTargetIntoView(next.target as string);
          setStepIndex(nextIndex);
          setRun(true);
        }, 400);
        return;
      }
      scrollTourTargetIntoView(next.target as string);
      setStepIndex(nextIndex);
    }
  };

  const steps = useMemo(() => DEMO_PHASE1_TOUR_STEPS, []);

  if (!enabled) return null;

  return (
    <>
      <Dialog open={chooserOpen} onClose={useGuide} maxWidth="sm" fullWidth>
        <DialogTitle>How do you want to explore?</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            You’re in the interactive demo sandbox. Pick a path:
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            <strong>Guided Tour</strong> — we highlight what to click on Dashboard, Customer
            onboarding, Scheduling, and Route Optimization.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Use the guide we sent</strong> — follow the full email walkthrough or the short
            text tips. You can start the Guided Tour anytime from Command Center (under Tutorials).
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={useGuide} color="inherit">
            I’ll use the emailed / texted guide
          </Button>
          <Button variant="contained" onClick={startTour} sx={{ fontWeight: 700 }}>
            Start Guided Tour
          </Button>
        </DialogActions>
      </Dialog>

      <Joyride
        steps={steps}
        run={run}
        stepIndex={stepIndex}
        continuous
        showSkipButton
        showProgress
        disableScrolling
        disableScrollParentFix
        disableOverlayClose
        callback={handleCallback}
        floaterProps={{
          disableAnimation: true,
          styles: {
            floater: {
              filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.25))',
            },
          },
        }}
        styles={{
          options: {
            primaryColor: '#2e7d32',
            zIndex: 1400,
          },
          tooltip: {
            borderRadius: 8,
            padding: 16,
          },
        }}
        locale={{
          back: 'Back',
          close: 'Close',
          last: 'Done',
          next: 'Next',
          skip: 'Skip tour',
        }}
      />
    </>
  );
};

export default DemoGuidedTour;
